import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { TrendingDown, TrendingUp } from "lucide-react";

const AXIS = { fontSize: 10, fill: "#9CA3AF" };
const TOOLTIP_STYLE = { background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: 6, fontSize: 12 };
const LABEL_STYLE = { color: "#6B7280", fontSize: 11 };
const LEGEND_STYLE = { fontSize: 10, color: "#4D4D4D" };
const MARGIN = { top: 5, right: 10, left: 0, bottom: 0 };

const num = (v, d = 0) => (v == null ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: d }));

/** This year against last year, month by month, with the saving spelled out. */
export function YearComparison({ siteId, year }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    setData(null);
    setErr(null);
    api
      .get(`/sites/${encodeURIComponent(siteId)}/energy/yoy`, { params: { year } })
      .then(({ data: payload }) => alive && setData(payload))
      .catch((e) => alive && setErr(e?.response?.data?.detail || e.message));
    return () => { alive = false; };
  }, [siteId, year]);

  const chart = useMemo(
    () => (data?.months || []).map((m) => ({ label: m.label, current: m.elec, previous: m.elec_prev })),
    [data]
  );

  if (err) return <div className="text-sm text-red-600" data-testid="yoy-error">{err}</div>;
  if (!data) return <div className="h-64 flex items-center justify-center k-sublabel">Comparing years…</div>;

  const c = data.comparable || {};
  const saving = c.saved_kwh;
  const improving = saving != null && saving > 0;
  const Icon = improving ? TrendingDown : TrendingUp;

  return (
    <div data-testid="yoy-panel">
      {c.months > 0 ? (
        <div
          className="flex items-center gap-3 rounded-lg px-4 py-3 mb-5"
          style={{ background: improving ? "rgba(21,111,91,0.08)" : "rgba(185,28,28,0.06)" }}
          data-testid="yoy-savings"
        >
          <Icon className="h-5 w-5" style={{ color: improving ? "#156F5B" : "#B91C1C" }} />
          <div className="text-sm text-[#212121]">
            <span className="font-heading font-bold">
              {improving ? "Saved" : "Used"} {num(Math.abs(saving))} kWh
            </span>{" "}
            ({c.delta_pct == null ? "—" : `${c.delta_pct > 0 ? "+" : ""}${c.delta_pct}%`}) versus {data.prev_year}
            {c.saved_cost != null && (
              <> · about {num(Math.abs(c.saved_cost))} {data.currency} {improving ? "kept" : "extra"}</>
            )}
            <span className="k-sublabel ml-1">across {c.months} matching month{c.months === 1 ? "" : "s"}</span>
          </div>
        </div>
      ) : (
        <p className="text-xs text-[#6B7280] mb-4" data-testid="yoy-no-overlap">
          No overlapping months with {data.prev_year} yet — the comparison fills in as history loads overnight.
        </p>
      )}

      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
          <BarChart data={chart} margin={MARGIN}>
            <CartesianGrid stroke="#F0F2F1" vertical={false} />
            <XAxis dataKey="label" tick={AXIS} />
            <YAxis tick={AXIS} width={56} />
            <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={LABEL_STYLE} />
            <Legend wrapperStyle={LEGEND_STYLE} />
            <Bar name={`${data.prev_year} kWh`} dataKey="previous" fill="#C7D6D1" radius={[3, 3, 0, 0]} />
            <Bar name={`${data.year} kWh`} dataKey="current" fill="#156F5B" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
